/**
 * Legacy Persona Model — DISPLAY ONLY
 *
 * This module defines inferred display labels ("personas") for Legacy users.
 * Personas exist to give admins and end-users a human-readable name for
 * their effective access level. They are derived by reading Central data;
 * they never write, gate, or bypass anything.
 *
 * ┌──────────────────────────────────────────────────────────────────┐
 * │  THESE FUNCTIONS MUST NEVER BE USED FOR ACCESS CONTROL.         │
 * │                                                                  │
 * │  Runtime authorization comes EXCLUSIVELY from:                   │
 * │    workspace.capabilities['levelup-legacy'][domain][action]      │
 * │    workspace.permissions[key]                                    │
 * │                                                                  │
 * │  If you need to check whether a user can perform an action,     │
 * │  use can() / hasCapability() / requirePermission().             │
 * │  NEVER branch on a persona string for access decisions.         │
 * └──────────────────────────────────────────────────────────────────┘
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CentralBaseRole = 'owner' | 'admin' | 'member' | 'viewer';

export type LegacyPersona =
  | 'Legacy Owner'
  | 'Legacy Admin'
  | 'Legacy Operator'
  | 'Legacy Analyst'
  | 'Legacy Contributor'
  | 'Legacy Viewer';

export interface CapabilityBundle {
  workspace: { read: boolean };
  agents:    { read: boolean; write: boolean };
  chat:      { read: boolean; write: boolean };
  plans:     { read: boolean; write: boolean };
  recommendations: { read: boolean; write: boolean };
  alerts:    { read: boolean; write: boolean };
  settings:  { read: boolean; write: boolean };
  snapshots: { read: boolean; write: boolean };
}

export interface WorkspacePermissionBundle {
  manageMembers: boolean;
  manageBilling: boolean;
  transferOwnership: boolean;
}

export interface PersonaDefinition {
  persona: LegacyPersona;
  centralRole: CentralBaseRole;
  description: string;
  expectedCapabilities: CapabilityBundle;
  expectedPermissions: WorkspacePermissionBundle;
}

// ---------------------------------------------------------------------------
// Capability bundle helpers
// ---------------------------------------------------------------------------

const ALL_READ: CapabilityBundle = {
  workspace: { read: true },
  agents:    { read: true,  write: false },
  chat:      { read: true,  write: false },
  plans:     { read: true,  write: false },
  recommendations: { read: true,  write: false },
  alerts:    { read: true,  write: false },
  settings:  { read: true,  write: false },
  snapshots: { read: true,  write: false },
};

const ALL_WRITE: CapabilityBundle = {
  workspace: { read: true },
  agents:    { read: true, write: true },
  chat:      { read: true, write: true },
  plans:     { read: true, write: true },
  recommendations: { read: true, write: true },
  alerts:    { read: true, write: true },
  settings:  { read: true, write: true },
  snapshots: { read: true, write: true },
};

const MEMBER_WRITE: CapabilityBundle = {
  workspace: { read: true },
  agents:    { read: true, write: true },
  chat:      { read: true, write: true },
  plans:     { read: true, write: true },
  recommendations: { read: true, write: true },
  alerts:    { read: true, write: true },
  settings:  { read: true, write: false },
  snapshots: { read: true, write: true },
};

// ---------------------------------------------------------------------------
// Persona definitions
// ---------------------------------------------------------------------------

export const LEGACY_PERSONAS: PersonaDefinition[] = [
  {
    persona: 'Legacy Owner',
    centralRole: 'owner',
    description: 'Full control over workspace, billing, and all Legacy features.',
    expectedCapabilities: ALL_WRITE,
    expectedPermissions: { manageMembers: true, manageBilling: true, transferOwnership: true },
  },
  {
    persona: 'Legacy Admin',
    centralRole: 'admin',
    description: 'Full app access including settings. Can manage workspace members.',
    expectedCapabilities: ALL_WRITE,
    expectedPermissions: { manageMembers: true, manageBilling: false, transferOwnership: false },
  },
  {
    persona: 'Legacy Operator',
    centralRole: 'member',
    description: 'Broad write access across agents, chat, plans, alerts, and snapshots.',
    expectedCapabilities: MEMBER_WRITE,
    expectedPermissions: { manageMembers: false, manageBilling: false, transferOwnership: false },
  },
  {
    persona: 'Legacy Analyst',
    centralRole: 'member',
    description: 'Read-heavy access with write to recommendations and plans for review workflows.',
    expectedCapabilities: {
      workspace: { read: true },
      agents:    { read: true, write: false },
      chat:      { read: true, write: true },
      plans:     { read: true, write: true },
      recommendations: { read: true, write: true },
      alerts:    { read: true, write: false },
      settings:  { read: true, write: false },
      snapshots: { read: true, write: false },
    },
    expectedPermissions: { manageMembers: false, manageBilling: false, transferOwnership: false },
  },
  {
    persona: 'Legacy Contributor',
    centralRole: 'member',
    description: 'Selective write access: can upload documents and chat, but cannot run agents.',
    expectedCapabilities: {
      workspace: { read: true },
      agents:    { read: true, write: false },
      chat:      { read: true, write: true },
      plans:     { read: true, write: false },
      recommendations: { read: true, write: false },
      alerts:    { read: true, write: false },
      settings:  { read: true, write: false },
      snapshots: { read: true, write: true },
    },
    expectedPermissions: { manageMembers: false, manageBilling: false, transferOwnership: false },
  },
  {
    persona: 'Legacy Viewer',
    centralRole: 'viewer',
    description: 'Read-only access across all Legacy domains.',
    expectedCapabilities: ALL_READ,
    expectedPermissions: { manageMembers: false, manageBilling: false, transferOwnership: false },
  },
];

// ---------------------------------------------------------------------------
// Inference — resolves persona from live Central data
// ---------------------------------------------------------------------------

const APP_ID = 'levelup-legacy';

type Capabilities = Record<string, Record<string, Record<string, boolean>>>;
type Permissions = Record<string, boolean>;

/** Domains used for bundle scoring */
const DOMAINS = ['agents', 'chat', 'plans', 'recommendations', 'alerts', 'settings', 'snapshots'] as const;

/**
 * Counts how many write capabilities are granted across all domains.
 */
function countWriteCapabilities(caps: Capabilities | undefined): number {
  if (!caps) return 0;
  const appCaps = caps[APP_ID];
  if (!appCaps) return 0;
  return DOMAINS.filter((d) => appCaps[d]?.write === true).length;
}

/**
 * Checks whether a specific set of write domains matches exactly.
 */
function hasExactWriteDomains(
  caps: Capabilities | undefined,
  domains: readonly string[],
): boolean {
  if (!caps) return false;
  const appCaps = caps[APP_ID];
  if (!appCaps) return false;
  return DOMAINS.every((d) => {
    const expected = domains.includes(d);
    const actual = appCaps[d]?.write === true;
    return expected === actual;
  });
}

/**
 * Infers the best-fit Legacy persona from live Central data.
 *
 * DISPLAY ONLY — do NOT use the return value for access control.
 * Use can() / hasCapability() / requirePermission() for authorization.
 *
 * Resolution order:
 * 1. Check role — narrows candidate set
 * 2. Check capabilities — scores against expected bundles
 * 3. Check workspace permissions — tiebreaker for owner vs admin
 *
 * @returns A persona label string, or null if data is insufficient.
 *          The return value is a UI label, NOT a permission grant.
 */
export function inferLegacyPersona(
  capabilities: Capabilities | undefined | null,
  _permissions: Permissions | undefined | null,
  role: string | undefined | null,
): LegacyPersona | null {
  if (!role) return null;

  const caps = capabilities ?? undefined;

  // Owner
  if (role === 'owner') {
    return 'Legacy Owner';
  }

  // Admin
  if (role === 'admin') {
    return 'Legacy Admin';
  }

  // Viewer
  if (role === 'viewer') {
    return 'Legacy Viewer';
  }

  // Member — differentiate by capability bundle
  if (role === 'member') {
    const writeCount = countWriteCapabilities(caps);

    // No capabilities available — cannot differentiate
    if (!caps || !caps[APP_ID]) return null;

    // Broad write (5+ domains writable, including agents) → Operator
    if (writeCount >= 5) {
      return 'Legacy Operator';
    }

    // Review-heavy: plans + recommendations + chat write, no agents/snapshots write
    const analystDomains = ['chat', 'plans', 'recommendations'] as const;
    if (hasExactWriteDomains(caps, analystDomains)) {
      return 'Legacy Analyst';
    }

    // Selective: chat + snapshots write only
    const contributorDomains = ['chat', 'snapshots'] as const;
    if (hasExactWriteDomains(caps, contributorDomains)) {
      return 'Legacy Contributor';
    }

    // Some write access but doesn't match a named pattern → Contributor (closest safe label)
    if (writeCount > 0 && writeCount < 5) {
      return 'Legacy Contributor';
    }

    // Read-only member (unusual but possible)
    return 'Legacy Viewer';
  }

  return null;
}

/**
 * Checks whether live Central data matches a specific persona's expected bundle.
 *
 * DISPLAY ONLY — do NOT use the return value for access control.
 * A true result means "the data looks like this persona" for admin UX;
 * it does NOT mean the user is authorized for anything.
 *
 * Returns true only if ALL expected capabilities and permissions match.
 * Missing Central data → false (no assumptions).
 */
export function matchesLegacyPersona(
  capabilities: Capabilities | undefined | null,
  permissions: Permissions | undefined | null,
  persona: LegacyPersona,
): boolean {
  const def = LEGACY_PERSONAS.find((p) => p.persona === persona);
  if (!def) return false;

  // Check capabilities
  if (!capabilities) return false;
  const appCaps = capabilities[APP_ID];
  if (!appCaps) return false;

  for (const domain of DOMAINS) {
    const expected = def.expectedCapabilities[domain];
    const actual = appCaps[domain];
    if (!actual) return false;
    if (actual.read !== expected.read) return false;
    if (actual.write !== expected.write) return false;
  }

  // Check workspace read
  if (appCaps['workspace']?.read !== def.expectedCapabilities.workspace.read) return false;

  // Check permissions
  if (!permissions) return false;
  if (!!permissions['manageMembers'] !== def.expectedPermissions.manageMembers) return false;
  if (!!permissions['manageBilling'] !== def.expectedPermissions.manageBilling) return false;
  if (!!permissions['transferOwnership'] !== def.expectedPermissions.transferOwnership) return false;

  return true;
}

/**
 * Returns the display label for a persona, or a capitalized base role
 * if persona cannot be inferred.
 *
 * DISPLAY ONLY — the returned string is a UI label.
 * Do NOT branch on it for access control.
 */
export function getPersonaLabel(
  capabilities: Capabilities | undefined | null,
  permissions: Permissions | undefined | null,
  role: string | undefined | null,
): string {
  const persona = inferLegacyPersona(capabilities, permissions, role);
  if (persona) return persona;
  if (role) return role.charAt(0).toUpperCase() + role.slice(1);
  return 'Unknown';
}

/**
 * Returns a short description for the inferred persona.
 *
 * DISPLAY ONLY — the returned string is a tooltip / helper text.
 * Do NOT branch on it for access control.
 */
export function getPersonaDescription(
  capabilities: Capabilities | undefined | null,
  permissions: Permissions | undefined | null,
  role: string | undefined | null,
): string | null {
  const persona = inferLegacyPersona(capabilities, permissions, role);
  if (!persona) return null;
  const def = LEGACY_PERSONAS.find((p) => p.persona === persona);
  return def?.description ?? null;
}
